import QueryString from 'qs';
import { z } from 'zod';

import authService from '@/services/auth/auth';

import { apiRoutes } from './config';
import type { DownloadCVFormat } from './cvGenerator';

const cezSchema = z.object({
  id: z.number(),
  publicId: z.string(),
  name: z.string(),
  jobTitle: z.string(),
  skills: z.array(
    z.object({
      skillName: z.string(),
      experienceYears: z.string(),
      additionalInfo: z.string(),
    })
  ),
  education: z.array(
    z.object({
      school: z.string(),
      specialty: z.string(),
      graduationYear: z.string(),
      degree: z.string(),
    })
  ),
  educationAdditionalNotes: z.string().nullable(),
  workExperience: z.array(
    z.object({
      companyName: z.string(),
      period: z.string(),
      role: z.string(),
      tasks: z.array(z.string()),
    })
  ),
  industryExperience: z.array(
    z.object({
      companyName: z.string(),
      period: z.string(),
      role: z.string(),
      tasks: z.array(z.string()),
    })
  ),
  certificates: z.array(
    z.object({
      name: z.string(),
      date: z.string(),
      expirationDate: z.string(),
      issuerAccreditation: z.string(),
      additionalInfo: z.string(),
    })
  ),
  projectExperience: z.array(
    z.object({
      companyName: z.string(),
      period: z.string(),
      role: z.string(),
      tasks: z.array(z.string()),
    })
  ),
  isTooLong: z.boolean(),
  generalStatus: z.string(),
  originalCvFileUrl: z.string(),
  workExperienceAdditionalNotes: z.string().nullable(),
  projectExperienceAdditionalNotes: z.string().nullable(),
  industryExperienceAdditionalNotes: z.string().nullable(),
  status: z.object({
    FAILED: z.array(z.string()),
    PROCESSING: z.array(z.string()),
    READY: z.array(z.string()),
  }),
});

export type GetCezCvContext = z.infer<typeof cezSchema>;
export type PatchCezCvContext = Omit<GetCezCvContext, 'id' | 'status'>;

export const getCezCvContext = async (id: number) => {
  const { data } = await authService.get(
    apiRoutes.cvGenerator.cez.detail(id)._root
  );
  return cezSchema.parse(data);
};

export const patchCezCvContext = async ({
  id,
  formData,
}: {
  id: number;
  formData: PatchCezCvContext;
}) => {
  const { data } = await authService.patch(
    apiRoutes.cvGenerator.cez.detail(id)._root,
    formData
  );
  return cezSchema.parse(data);
};

export const cezDownloadCV = async ({
  id,
  type,
  template,
  signal,
}: {
  id: number;
  type: DownloadCVFormat;
  template: 'INETUM_CEZ';
  hideSkillsYearsOfExp?: boolean;
  signal?: AbortSignal;
}) => {
  return authService.get<Blob>(apiRoutes.cvGenerator.cez.detail(id).download, {
    params: {
      type,
      template,
    },
    signal,
    paramsSerializer: (params) => {
      return QueryString.stringify(params, { arrayFormat: 'repeat' });
    },
    responseType: 'blob',
  });
};

export const postCezCVContextFromCandidateId = async ({
  forceGenerate,
  id,
}: {
  id: number;
  forceGenerate: boolean;
}) => {
  const { data } = await authService.post(
    apiRoutes.cvGenerator.cez.fromCandidateId.detail(id),
    { forceGenerate }
  );
  return cezSchema.parse(data);
};

export const postRetryCezCVContext = async (id: number) => {
  return authService.post(apiRoutes.cvGenerator.cez.detail(id).retry);
};
